import React, { useState, useEffect, useCallback } from 'react'
import { Button, Dimmer, Header, Loader, Message, Pagination } from 'semantic-ui-react'
import { Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material'
import Data, { IDataset } from '../../api/Data'
import { HelpDocsLink } from '../../components/common/ExternalLink'
import Section from '../../components/common/Section'
import SubSection from '../../components/common/SubSection'
import Title from '../../components/common/Title'
import Auth from '../../utils/Auth'
import UploadCSVModal from '../../components/modals/UploadCSVModal'
import { useDatasetsCreator } from '../../contexts/DatasetsCreatorContext'
import { useHistory } from 'react-router'
import './DatasetsPage.scss'

const DatasetList = () => {
  // States
  const [datasets, setDatasets] = useState<IDataset[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const { setCurrentStep, resetState } = useDatasetsCreator()
  const history = useHistory()

  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState(1)
  const rowsPerPage = 20

  if (!setCurrentStep || !resetState) {
    throw new Error('DatasetsCreator context was not initialized properly')
  }

  const fetchDatasets = useCallback(async () => {
    const { teamId } = Auth.getPayLoad() || {}

    if (!teamId) {
      setError('Unable to find team ID. Please refresh the page and try again!')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const skip = (currentPage - 1) * rowsPerPage
      const { data, error } = await Data.getTeamDatasets(teamId, skip, rowsPerPage)

      if (error || !data) {
        if (error.response && error.response.data) {
          const errorMessage = error.response.data

          if (errorMessage.includes('not authorized') || errorMessage.includes('Team not found')) {
            setError('Something went wrong with your session. Please logout and back in before trying again.')
          } else {
            setError('An error occurred. Please try again later.')
          }
        }
        setDatasets([])
        setTotalPages(0)
        return
      }

      setDatasets(data.datasets)
      setTotalPages(Math.ceil(data.total / rowsPerPage))
    } catch (error) {
      setError('An error occurred. Please try again later.')
      console.error('Failed to load datasets:', error)
    } finally {
      setLoading(false)
    }
  }, [currentPage])

  useEffect(() => {
    fetchDatasets()
  }, [fetchDatasets])

  const handlePageChange = (_: React.SyntheticEvent, data: any) => {
    setCurrentPage(data.activePage)
  }

  const handleCreateDataset = () => {
    resetState()
    setCurrentStep('upload')
  }

  const handleRowClick = (dataset: IDataset) => {
    history.push(`/datasets/${dataset.id}`)
  }

  const formatUsedInText = (projectNames: string[]): string => {
    if (!projectNames || projectNames.length === 0) {
      return 'None'
    }

    if (projectNames.length <= 3) {
      return projectNames.join(', ')
    }

    const visibleProjects = projectNames.slice(0, 3).join(', ')
    const remainingCount = projectNames.length - 3
    return `${visibleProjects}, and ${remainingCount} more`
  }

  if (error) {
    return (
      <Section>
        <div>
          <Title title="Datasets" />
          <SubSection>
            <Message negative style={{ textAlign: 'center' }}>
              <Message.Header>{error}</Message.Header>
            </Message>
          </SubSection>
        </div>
      </Section>
    )
  }

  const renderDatasetsTable = () => {
    return (
      <div className="ui segment">
        {datasets.length > 0 ? (
          <>
            {totalPages > 1 && (
              <div className="pagination-container">
                <Pagination
                  activePage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  size="mini"
                  firstItem={null}
                  lastItem={null}
                  prevItem={{ content: '<', icon: true }}
                  nextItem={{ content: '>', icon: true }}
                />
              </div>
            )}
            <Table size="small">
              <TableHead>
                <TableRow className="dataset-table-header">
                  <TableCell className="dataset-table-cell">Title</TableCell>
                  <TableCell className="dataset-table-cell">Description</TableCell>
                  <TableCell align="right" className="dataset-table-cell-right">
                    Key Column
                  </TableCell>
                  <TableCell align="right" className="dataset-table-cell-right">
                    Used In
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {datasets.map((dataset: IDataset, i: number) => (
                  <TableRow key={i} onClick={() => handleRowClick(dataset)} className="dataset-table-row">
                    <TableCell className="dataset-table-data">{dataset.title}</TableCell>
                    <TableCell className="dataset-table-data">{dataset.description}</TableCell>
                    <TableCell align="right" className="dataset-table-data-right">
                      {dataset.keyColumn}
                    </TableCell>
                    <TableCell align="right" className="dataset-table-data-right">
                      <Tooltip
                        title={dataset.usedIn && dataset.usedIn.length > 3 ? dataset.usedIn.join(', ') : ''}
                        placement="top"
                        arrow
                      >
                        <div>{formatUsedInText(dataset.usedIn || [])}</div>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        ) : (
          <div className="empty-state">
            <p>
              Upload a CSV file containing structured data—such as customer or employee records—to save your
              contributors time. With just one identifier (like an email or employee ID), all associated details can be
              pre-filled automatically.
            </p>
            <p>Hit the "Create a New Dataset" button on the top navigation bar to get started.</p>
          </div>
        )}
      </div>
    )
  }

  return (
    <Section>
      <>
        <Title title="My Datasets">
          <>
            <Button primary compact onClick={handleCreateDataset} className="create-dataset-button">
              Create a New Dataset
            </Button>
          </>
        </Title>

        <SubSection hasGreyBackground>
          <>
            {loading ? (
              <Dimmer active={loading}>
                <Loader size="large">Loading datasets...</Loader>
              </Dimmer>
            ) : (
              <>
                <Header as="h1" style={{ marginBottom: '2rem', fontWeight: 'bold' }}>
                  My Datasets
                  <Header.Subheader>
                    <>
                      Prepare and configure your team’s predefined data fields. Set up smart, interconnected datasets to
                      streamline your video submission projects. View{' '}
                      <HelpDocsLink documentTag="how-to-format-a-csv-dataset-for-use-in-vloggi">
                        formatting guide
                      </HelpDocsLink>{' '}
                      and{' '}
                      <HelpDocsLink documentTag="how-to-create-a-lookup-table-by-uploading-existing-datasets-of-contributor-information-to-vloggi">
                        learn how to use datasets
                      </HelpDocsLink>{' '}
                      for more detail.
                    </>
                  </Header.Subheader>
                </Header>
                {renderDatasetsTable()}
              </>
            )}
          </>
        </SubSection>
      </>
    </Section>
  )
}

const DatasetsPage = () => {
  return (
    <div>
      <DatasetList />
      <UploadCSVModal />
    </div>
  )
}

export default DatasetsPage
